import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { CourseActivity, CourseDetail } from '@/lib/api';

import * as coursePlayerSelectors from './selectors';
import * as thunks from './thunks';
import type { CoursePlayerState } from './types';

const initialState: CoursePlayerState = {
    course: undefined,
    progress: 0,
};

const { actions: coursePlayerActions, reducer: coursePlayerReducer } = createSlice({
    name: 'coursePlayer',
    initialState,
    reducers: {
        reset: () => initialState,

        initializePlayer: (state, action: PayloadAction<CourseDetail>) => {
            const course = action.payload;
            course.sections?.sort((a, b) => a.course_order - b.course_order);
            state.course = course;

            const { activity_positions: activityPositions = {} } = course;

            let activeSection = course.sections?.[0];

            const positions = Object.keys(activityPositions as object).map((activityId) => ({
                activityId,
                ...activityPositions[activityId],
            }));
            positions.sort((a, b) => {
                const aUpdatedAt = new Date(a.updated_at);
                const bUpdatedAt = new Date(b.updated_at);
                return aUpdatedAt.getTime() - bUpdatedAt.getTime();
            });

            const lastPosition = positions[positions.length - 1];
            let activeActivity: CourseActivity | undefined;

            if (lastPosition) {
                for (const section of course.sections ?? []) {
                    for (const activity of section.activities ?? []) {
                        if (activity.id === lastPosition.activityId) {
                            activeSection = section;
                            activeActivity = activity;
                            break;
                        }
                    }
                }
            }
            state.activeSection = activeSection;
            state.activeActivity = activeActivity ?? activeSection?.activities?.[0];
        },

        showActivity: (state, action: PayloadAction<{ activity: CourseActivity }>) => {
            const sections = state.course?.sections ?? [];
            const { activity } = action.payload;

            const sectionIndex = sections.findIndex((section) => section.id === activity.section_id);
            const section = sections[sectionIndex];
            if (!section) {
                return;
            }

            state.activeSection = section;
            state.activeActivity = activity;
            state.progress = ((sectionIndex + 1) / sections.length) * 100;
        },
    },
    extraReducers: (builder) => {
        // Set course progress
        builder.addCase(thunks.setCourseProgress.pending, () => {});
        builder.addCase(thunks.setCourseProgress.fulfilled, (state, { payload }) => {
            if (!state.course) {
                return;
            }
            if (!state.course.activity_positions) {
                state.course.activity_positions = {};
            }
            state.course.activity_positions[payload.activity_id] = {
                visited: true,
                video_time_seconds: payload.video_time_seconds,
                updated_at: payload.updated_at,
            };
        });
        builder.addCase(thunks.setCourseProgress.rejected, () => {});
    },
});

export { coursePlayerActions, coursePlayerReducer, coursePlayerSelectors };
